import isFunction from 'lodash-es/isFunction'
import { ForwardedRef, forwardRef, ReactNode, useEffect, useRef } from 'react'
import ReactSelect, { components, Props, Theme } from 'react-select'

import { SelectOption } from '@/lib/core/components/Select'
import { IndicatorsContainer, MenuList, Option } from '@/lib/core/components/Select/components'
import { getSelectTheme, stylesConfig } from '@/lib/core/components/Select/Select.styles'

export type SelectProps = Props<SelectOption> & {
  actionOption?: ReactNode
  actionCallback?: () => void
  selectTheme?: (theme: Theme) => Theme
  menuPlacement?: 'bottom' | 'top'
  hasDisabledValue?: boolean
  hasError?: boolean
  leftAdorment?: boolean
  dataTestId?: string
} & ({ 'aria-label': string } | { 'aria-labelledby': string })

const SelectWrappedBase = (
  {
    actionOption,
    actionCallback = () => ({}),
    menuPlacement = 'bottom',
    hasDisabledValue,
    selectTheme = (theme) => getSelectTheme(theme, { menuPlacement }),
    hasError,
    leftAdorment,
    ...props
  }: SelectProps,
  forwardedRef?: ForwardedRef<HTMLElement>,
) => {
  const select = useRef<HTMLElement>(null)

  useEffect(() => {
    if (forwardedRef) {
      if (isFunction(forwardedRef)) {
        forwardedRef(select.current)
      } else {
        forwardedRef.current = select.current
      }
    }
  }, [forwardedRef, select.current])

  return (
    <ReactSelect
      // @ts-expect-error This is a workaround for the type issue with ReactSelect
      ref={select}
      theme={selectTheme}
      menuPlacement={menuPlacement}
      menuPosition="absolute"
      menuShouldScrollIntoView={false}
      styles={stylesConfig}
      components={{
        Control: (controlProps) =>
          components.Control({
            ...controlProps,
            // @ts-expect-error This has been working ergo fine
            hasDisabledValue,
            hasError,
            leftAdorment,
          }) as ReactNode,
        IndicatorsContainer,
        MenuList: MenuList({
          actionCallback: () => {
            select?.current?.blur()
            actionCallback()
          },
          actionOption,
        }),
        Option,
      }}
      {...props}
    />
  )
}

export const SelectDefault = forwardRef(SelectWrappedBase)
